import { useCallback, useEffect, useRef, useState } from 'react'
import { KeyRound, Tablet } from 'lucide-react'
import { Link } from '../router.js'
import { adminMutate, apiRead, usePermissions } from '../admin/primitives.js'
import { Field, TextInput } from '../admin/form.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import { messageErreur } from './tablettes-modele.js'
import './tablettes.css'

/**
 * ADMINISTRATION › INTÉGRATIONS › TABLETTES (02/11/2026).
 *
 * Même modèle que l'interphone : l'adresse de kiosk-api (une VALEUR → la Save
 * Bar) et le jeton de Gestion (un GESTE → « Tester » puis « Enregistrer »,
 * confirmation d'identité). Le jeton n'est jamais réaffiché. kiosk-api absent
 * ou muet : la page reste utilisable et le dit.
 */

interface Connexion {
  configured: boolean
  api_url: string | null
  token: { set: boolean; updated_at: string | null }
  status: { reachable: boolean; error_code: string | null; message: string | null; mqtt_connecte: boolean | null; version: string | null; contrat: string | null }
  contrat_local: string
}

const QUAND = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const quandParis = (iso: string | null | undefined): string => {
  const d = iso ? Date.parse(iso) : NaN
  return Number.isFinite(d) ? `le ${QUAND.format(new Date(d))}` : '—'
}

/** « Jeton refusé » n'est pas « Injoignable » : l'un se règle ici, l'autre chez kiosk-api. */
function pastilleConnexion(c: Connexion | null): { ton: 'ok' | 'warning' | 'error' | 'idle'; texte: string } {
  if (!c) return { ton: 'idle', texte: '—' }
  if (!c.configured) return { ton: 'idle', texte: 'Non raccordé' }
  if (c.status.error_code === 'TABLETTES_TOKEN_REFUSED') return { ton: 'error', texte: 'Jeton refusé' }
  if (c.status.error_code === 'TABLETTES_ADRESSE_REFUSEE') return { ton: 'error', texte: 'Adresse refusée' }
  if (c.status.error_code === 'TABLETTES_DISABLED') return { ton: 'idle', texte: 'Coupé (Mode sûr)' }
  if (c.status.reachable) return c.status.mqtt_connecte === false ? { ton: 'warning', texte: 'Broker injoignable' } : { ton: 'ok', texte: 'Raccordé' }
  return { ton: 'warning', texte: 'Injoignable' }
}

type Lecture = { etat: 'chargement' } | { etat: 'ok'; data: Connexion } | { etat: 'erreur'; message: string }

// Ports refusés par fetch (norme Fetch, « bad port ») : copie de
// FETCH_PORTS_INTERDITS (packages/core/src/interphone-service/config.ts, repris par tablettes-service),
// le web n'important pas le cœur. L'API refuse de toute façon.
const PORTS_INTERDITS = new Set([
  1, 7, 9, 11, 13, 15, 17, 19, 20, 21, 22, 23, 25, 37, 42, 43, 53, 69, 77, 79, 87, 95, 101, 102, 103, 104, 109, 110, 111, 113, 115, 117, 119, 123, 135, 137, 139, 143, 161, 179, 389, 427, 465, 512, 513, 514, 515, 526, 530, 531, 532, 540, 548, 554, 556, 563, 587, 601, 636, 989, 990, 993, 995, 1719, 1720, 1723, 2049, 3659, 4045, 4190, 5060, 5061, 6000, 6566, 6665, 6666, 6667, 6668, 6669, 6679, 6697, 10080,
])

const validerAdresse = (v: string): string | null => {
  if (!v) return 'L’adresse de kiosk-api est requise (par exemple http://192.168.60.90:8098).'
  try {
    const u = new URL(v)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return 'L’adresse doit commencer par http:// ou https://.'
    if (u.username || u.password || u.search || u.hash) return 'L’adresse ne doit contenir ni identifiant, ni requête, ni fragment.'
    if (PORTS_INTERDITS.has(u.port ? Number(u.port) : (u.protocol === 'https:' ? 443 : 80))) return `Le port ${u.port} est bloqué par les navigateurs et par Gestion : choisissez-en un autre (par exemple 8098).`
  } catch { return 'Adresse invalide (par exemple http://192.168.60.90:8098).' }
  return null
}

export function TablettesIntegrationPage() {
  const { permissions } = usePermissions()
  const peutModifier = permissions.includes('tablettes.enroll')
  const [l, setL] = useState<Lecture>({ etat: 'chargement' })
  const [adresse, setAdresse] = useState('')
  const adresseLue = useRef('')

  const appliquer = useCallback((c: Connexion) => {
    setL({ etat: 'ok', data: c })
    const lue = c.api_url ?? ''
    const precedente = adresseLue.current
    adresseLue.current = lue
    // Une relecture n'écrase pas une adresse en cours de saisie.
    setAdresse((brouillon) => (brouillon === precedente ? lue : brouillon))
  }, [])
  const charger = useCallback(() => {
    void apiRead<Connexion>('/api/v1/tablettes/connexion')
      .then((r) => { if (r.status === 200) appliquer(r.data); else setL({ etat: 'erreur', message: messageErreur(r.status, r.data, 'Le raccordement est illisible.') }) })
      .catch(() => setL({ etat: 'erreur', message: 'Gestion n’a pas répondu.' }))
  }, [appliquer])
  useEffect(() => { charger() }, [charger])

  const lue = l.etat === 'ok' ? l.data.api_url ?? '' : ''
  const propre = adresse.trim().replace(/\/+$/, '')
  const dirty = peutModifier && l.etat === 'ok' && propre !== lue
  useSaveBar({
    dirty,
    invalid: dirty ? validerAdresse(propre) : null,
    saveLabel: 'Enregistrer l’adresse',
    successMessage: 'Adresse de kiosk-api enregistrée.',
    onSave: async () => {
      const r = await adminMutate<Connexion & { error?: string; message?: string }>('PUT', '/api/v1/tablettes/connexion', { api_url: propre })
      if (r.status === 200) { appliquer(r.data); setAdresse(r.data.api_url ?? ''); return true }
      if (r.data.error === 'STEPUP_REQUIRED') return 'Confirmation d’identité annulée : rien n’a été enregistré.'
      return messageErreur(r.status, r.data, 'L’adresse n’a pas été enregistrée.')
    },
    onCancel: () => setAdresse(lue),
  })

  const c = l.etat === 'ok' ? l.data : null
  // « Jeton refusé » n'est pas « Injoignable » : l'un se règle ici, l'autre sur le serveur.
  const p = pastilleConnexion(c)
  const pastille = { cls: `lc-pill--${p.ton}`, texte: p.texte }

  return (
    <main className="lc-page lc-page--readable lc-stagger tb-page">
      <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
      <h1 className="lc-title">Tablettes murales (kiosk-api)</h1>
      <p className="lc-subtitle">
        kiosk-api, sur la VM outils, est le seul à parler aux tablettes (MQTT). Gestion s’y raccorde avec un jeton,
        côté serveur, pour afficher leur état et relayer les ordres. Gestion n’a aucun compte sur le broker.
      </p>

      <section className="lc-panel tb-section" aria-labelledby="tb-raccord">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="tb-raccord"><Tablet size={16} aria-hidden="true" /> Connexion</h2>
          <span className={`lc-pill ${pastille.cls}`}>{pastille.texte}</span>
        </header>
        <div className="lc-panel__body tb-reglages">
          {l.etat === 'chargement' ? <p className="lc-hint" aria-busy="true">Lecture du raccordement…</p>
            : l.etat === 'erreur' ? <p className="tb-gris" role="status">{l.message}</p> : null}
          {c ? (
            <>
              <Field label="Adresse du service" helper="Adresse de kiosk-api sur la VM outils. Le jeton voyage dans l’en-tête, jamais dans l’adresse.">
                <TextInput value={adresse} onChange={setAdresse} disabled={!peutModifier} placeholder="http://192.168.60.90:8098" ariaLabel="Adresse de kiosk-api" />
              </Field>
              {c.status.reachable ? (
                <p className="cle-verdict cle-verdict--ok" role="status">
                  kiosk-api répond{c.status.version ? ` (version ${c.status.version})` : ''}{c.status.mqtt_connecte === null ? '.' : c.status.mqtt_connecte ? ' et joint le broker des tablettes.' : ', mais ne joint plus le broker des tablettes.'}
                </p>
              ) : c.status.message ? <p className={`cle-verdict${(c.status.error_code === 'TABLETTES_TOKEN_REFUSED' || c.status.error_code === 'TABLETTES_ADRESSE_REFUSEE') ? ' cle-verdict--ko' : ''}`} role="status">{c.status.message}</p> : null}
              {c.status.contrat ? (
                <dl className="cle-faits">
                  <div>
                    <dt>Contrat</dt>
                    <dd>{c.status.contrat === c.contrat_local ? `kiosk-api ${c.status.contrat}, celui de Gestion` : `kiosk-api sert le contrat ${c.status.contrat}, Gestion connaît le ${c.contrat_local} : à recopier avant de s’y fier.`}</dd>
                  </div>
                </dl>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {c ? <JetonPanneau etat={c.token} peutModifier={peutModifier} onEnregistre={appliquer} /> : null}
    </main>
  )
}

function JetonPanneau({ etat, peutModifier, onEnregistre }: {
  etat: { set: boolean; updated_at: string | null }
  peutModifier: boolean
  onEnregistre: (c: Connexion) => void
}) {
  const confirm = useConfirm()
  // Toujours vide au départ : la valeur ne vient JAMAIS du serveur.
  const [jeton, setJeton] = useState('')
  const [essai, setEssai] = useState(false)
  const [envoi, setEnvoi] = useState(false)
  const [verdict, setVerdict] = useState<{ ok: boolean | null; texte: string; pour: string } | null>(null)
  const [apres, setApres] = useState<{ ok: boolean | null; texte: string } | null>(null)
  const valeur = jeton.trim()
  const tropCourt = valeur.length < 32
  const courant = verdict && verdict.pour === valeur ? verdict : null

  const tester = async () => {
    setEssai(true); setApres(null)
    try {
      const r = await adminMutate<{ ok?: boolean; message?: string; error?: string }>('POST', '/api/v1/tablettes/jeton/test', { token: valeur })
      if (r.status === 200 && typeof r.data.ok === 'boolean') setVerdict({ ok: r.data.ok, texte: r.data.message ?? '', pour: valeur })
      else setVerdict({ ok: null, texte: messageErreur(r.status, r.data, 'L’essai n’a pas eu lieu.'), pour: valeur })
    } catch { setVerdict({ ok: null, texte: 'Gestion n’a pas répondu : l’essai n’a pas eu lieu.', pour: valeur }) } finally { setEssai(false) }
  }

  const enregistrer = async () => {
    if (courant?.ok !== true) {
      const ok = await confirm({
        title: 'Enregistrer un jeton qui n’a pas été reconnu ?',
        message: courant
          ? `Le dernier essai n’a pas abouti : ${courant.texte} S’il est faux, Gestion ne pourra plus lire ni piloter les tablettes.`
          : 'Ce jeton n’a pas été essayé. S’il est faux, Gestion ne pourra plus lire ni piloter les tablettes. Mieux vaut cliquer d’abord sur « Tester ».',
        variant: 'sensitive', confirmLabel: 'Enregistrer quand même',
      })
      if (!ok) return
    }
    setEnvoi(true); setApres(null)
    try {
      const r = await adminMutate<Connexion & { error?: string; message?: string }>('PUT', '/api/v1/tablettes/jeton', { token: valeur })
      if (r.status === 200) {
        // Vidé AVANT tout le reste : le secret ne séjourne pas dans la page.
        setJeton(''); setVerdict(null)
        setApres({ ok: true, texte: 'Jeton rangé au coffre de Gestion. Il ne sera jamais réaffiché.' })
        onEnregistre(r.data)
        return
      }
      setApres({ ok: r.data.error === 'STEPUP_REQUIRED' ? null : false, texte: r.data.error === 'STEPUP_REQUIRED' ? 'Confirmation d’identité annulée : rien n’a été enregistré.' : messageErreur(r.status, r.data, 'Le jeton n’a pas été enregistré.') })
    } catch { setApres({ ok: null, texte: 'Gestion n’a pas répondu : relisez la page avant de réessayer.' }) } finally { setEnvoi(false) }
  }

  const ton = (ok: boolean | null) => (ok === true ? ' cle-verdict--ok' : ok === false ? ' cle-verdict--ko' : '')
  return (
    <section className="lc-panel cle-panel tb-section" aria-labelledby="tb-jeton">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="tb-jeton"><KeyRound size={16} aria-hidden="true" /> Jeton de Gestion</h2>
        <span className={`lc-pill ${etat.set ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{etat.set ? 'Enregistré' : 'Absent'}</span>
      </header>
      <div className="lc-panel__body cle-corps">
        <dl className="cle-faits">
          <div>
            <dt>Jeton actuel</dt>
            <dd>{etat.set ? `Enregistré${etat.updated_at ? ` ${quandParis(etat.updated_at)}` : ''}` : 'Aucun jeton : Gestion ne peut pas encore lire les tablettes.'}</dd>
          </div>
        </dl>
        {peutModifier ? (
          <div className="cle-saisie">
            <label className="cle-champ">
              <span>Nouveau jeton</span>
              <input className="lc-input" type="password" autoComplete="new-password" spellCheck={false} name="tablettes-jeton"
                value={jeton} aria-label="Nouveau jeton de kiosk-api"
                onChange={(e) => { setJeton(e.target.value); setApres(null) }} />
            </label>
            <div className="cle-actions">
              <button type="button" className="lc-btn" disabled={tropCourt || essai || envoi} onClick={() => { void tester() }}>{essai ? 'Essai en cours…' : 'Tester'}</button>
              <button type="button" className="lc-btn lc-btn--primary" disabled={tropCourt || essai || envoi} onClick={() => { void enregistrer() }}>{envoi ? 'Enregistrement…' : 'Enregistrer le jeton'}</button>
            </div>
            {courant ? <p role="status" className={`cle-verdict${ton(courant.ok)}`}>{courant.texte}</p> : null}
          </div>
        ) : <p className="lc-hint">Seul un compte autorisé à enrôler des tablettes peut remplacer ce jeton.</p>}
        {apres ? <p role="status" className={`cle-verdict${ton(apres.ok)}`}>{apres.texte}</p> : null}
      </div>
    </section>
  )
}
